/**
 * <CountryFlag /> — small SVG flag from flagcdn.com.
 *
 * Shared component used across the admin dashboard so dealer and
 * customer rows show their country at a glance instead of relying
 * on the bare ISO code (e.g. "GBZ", "IRL") which is unfriendly to
 * read.
 *
 * Visual contract:
 *   • Default size: 18px x 13px (h-3.5) — tuned to sit nicely next
 *     to body text without dominating the row.
 *   • Bordered rounded corner for a polished look on dark UI.
 *   • Fallback: if the CDN ever fails or the code is unknown, a
 *     compact text badge with the ISO code is rendered instead so
 *     the row stays readable.
 *
 * @param {Object} props
 * @param {string} props.code        - Country code as stored in our DB.
 *                                     Most are ISO 3166-1 alpha-2 already
 *                                     (IT, FR, PL…) — three custom values
 *                                     (UK, GBZ, IRL) are remapped below.
 * @param {string} [props.className] - Extra Tailwind classes to apply.
 * @param {string} [props.title]     - Custom tooltip; defaults to the
 *                                     uppercased code.
 */

const FLAGCDN_CODE_OVERRIDES = {
  UK: "gb",
  GBZ: "gi",
  IRL: "ie",
};

export const CountryFlag = ({ code, className = "", title }) => {
  if (!code) {
    return (
      <span
        className={`inline-flex w-[18px] h-[13px] items-center justify-center text-[8px] font-bold text-white/40 bg-white/5 rounded-sm border border-white/10 ${className}`}
        title="Paese non specificato"
        data-testid="country-flag-missing"
      >
        ?
      </span>
    );
  }
  const upper = String(code).toUpperCase();
  const iso = (FLAGCDN_CODE_OVERRIDES[upper] || upper || "").toLowerCase();
  const url = iso ? `https://flagcdn.com/${iso}.svg` : null;
  const altText = title || upper;

  return url ? (
    <img
      src={url}
      alt={`Bandiera ${altText}`}
      title={altText}
      // Inline-block flex so it sits cleanly next to text. The fixed
      // height of 13px aligns visually with text-xs / text-sm content.
      className={`inline-block w-[18px] h-[13px] object-cover rounded-sm border border-white/10 align-middle flex-shrink-0 ${className}`}
      loading="lazy"
      onError={(e) => {
        // Swap the broken <img> for the badge fallback so layout
        // stays consistent even if the CDN ever goes dark.
        const span = document.createElement("span");
        span.textContent = upper.slice(0, 3);
        span.className = "inline-flex w-[18px] h-[13px] items-center justify-center text-[8px] font-bold text-white/60 bg-white/5 rounded-sm border border-white/10 align-middle";
        span.title = altText;
        e.currentTarget.replaceWith(span);
      }}
      data-testid={`country-flag-${upper}`}
    />
  ) : (
    <span
      className={`inline-flex w-[18px] h-[13px] items-center justify-center text-[8px] font-bold text-white/60 bg-white/5 rounded-sm border border-white/10 align-middle ${className}`}
      title={altText}
      data-testid={`country-flag-${upper}-text`}
    >
      {upper.slice(0, 3)}
    </span>
  );
};

export default CountryFlag;
